import { z } from 'zod'
import {
  AccountIntegrationConfigMappingType,
  type CRMAccountIntegrationConfig,
  getMappingTypeLabel,
  MappingType,
  OperationType as AccountOperationType,
  UpdateAdditionalInformation,
} from '../../../../models/Account'
import { BusinessEventType } from '../../../../models/BusinessEvent'
import { ZOCKS_EVENT_FIELDS } from '../../../../common/integrations'
import { isCompleteLinkFor } from '../../../../common/contactLinks'
import { isMappingTypeLocked } from './utils'

export const SessionMappingType = z.enum(['PREDEFINED', 'CUSTOM'])
export const DescriptorType = z.enum(['static', 'field', 'variable'])
export const FieldUpdateMode = z.enum(['append', 'prepend', 'overwrite', 'create_only'])
export const GroupOperator = z.enum(['and', 'or'])
export const OperationType = z.enum(['create', 'update', 'fetch'])
export const FilterOperator = z.enum(['eq', 'ne', 'gt', 'lt', 'ge', 'le', 'in'])
export const Direction = z.enum(['ASC', 'DESC'])

export const FieldConstraintSchema = z.object({
  maxLength: z.number().nullable().optional(),
  valueType: z.string().nullable().optional(),
  valueFormat: z.string().nullable().optional(),
})

export const MAX_ORDER_BY_FIELDS = 3

export const OrderBySchema = z.object({
  field: z.string(),
  direction: Direction,
})

export const OrderByListSchema = z.array(OrderBySchema).max(MAX_ORDER_BY_FIELDS)

export const ConditionValueSchema = z.object({
  value: z.string().nullable().optional(),
  type: DescriptorType,
  object: z.string().nullable().optional(),
  field: z.string().nullable().optional(),
  valueType: z.string().nullable().optional(),
  valueFormat: z.string().nullable().optional(),
})

export const FilterExpressionSchema = z.object({
  field: z.string(),
  operator: FilterOperator,
  source: ConditionValueSchema,
  valueType: z.string().nullable().optional(),
  valueFormat: z.string().nullable().optional(),
})

export const FilterSchema = z.object({
  name: z.string().nullable().optional(),
  groupOperator: GroupOperator,
  expressions: z.array(FilterExpressionSchema),
})

export const FilterGroupSchema = z.object({
  groupOperator: GroupOperator,
  filters: z.array(FilterSchema),
})

export const ConditionSchema = z.object({
  subject: ConditionValueSchema,
  operator: FilterOperator,
  source: ConditionValueSchema.nullable().optional(),
})

type ConditionGroup = {
  operator?: 'and' | 'or' | null
  conditions?: z.infer<typeof ConditionSchema>[] | null
  returnValue?: z.infer<typeof ConditionValueSchema> | null
  fallback?: ConditionGroup | z.infer<typeof ConditionValueSchema> | null
}

export const ConditionGroupSchema: z.ZodType<ConditionGroup, ConditionGroup> = z.lazy(() =>
  z.object({
    operator: GroupOperator.nullable().optional(),
    conditions: z.array(ConditionSchema).nullable().optional(),
    returnValue: ConditionValueSchema.nullable().optional(),
    fallback: z.union([ConditionGroupSchema, ConditionValueSchema]).nullable().optional(),
  }),
)

// ---------------------------------------------------------
// 4. DESCRIPTORS
// ---------------------------------------------------------

export const FieldDescriptorSchema = z.object({
  value: z.string().nullable().optional(),
  type: DescriptorType,
  valueType: z.string().nullable().optional(),
  valueFormat: z.string().nullable().optional(),
  updateMode: FieldUpdateMode.nullable().optional(),
  conditions: ConditionGroupSchema.nullable().optional(),
  defaultValue: z.string().nullable().optional(),
})

const RelationshipBase = z.object({
  object: z.string().nullable().optional(),
  field: z.string().nullable().optional(),
  valueType: z.string().nullable().optional(),
  valueFormat: z.string().nullable().optional(),
})

export const RelationshipTargetSchema = RelationshipBase.extend({})

export const RelationshipSourceSchema = RelationshipBase.extend({
  filters: z.array(FilterSchema).nullable().optional(),
})

export const ContactLinkSchema = z.object({
  source: RelationshipSourceSchema.nullable().optional(),
  target: RelationshipTargetSchema.nullable().optional(),
})

export const ContactSyncSchema = z.object({
  filterGroup: FilterGroupSchema.nullable().optional(),
  ownerFilterGroup: FilterGroupSchema.nullable().optional(),
  applyOwnerFilter: z.boolean().nullable().optional(),
})

export const RelatedObjectSchema = z.object({
  include: z.boolean().nullable().optional(),
  dependency: z.array(z.string()).nullable().optional(),
  limit: z.number().nullable().optional(),
  outputFields: z.array(z.record(z.string(), z.string())).nullable().optional(),
  orderBy: OrderBySchema.nullable().optional(),
  filters: z.array(FilterSchema).nullable().optional(),
  fields: z.record(z.string(), z.array(FieldDescriptorSchema)).nullable().optional(),
  operationType: OperationType.nullable().optional(),
  isHousehold: z.boolean().nullable().optional(),
  primaryIdAttribute: z.string().nullable().optional(),
})

export const DetailedCRMAccountIntegrationMappingSchema = z.object({
  objectName: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  fields: z.record(z.string(), z.array(FieldDescriptorSchema)).nullable().optional(),
  // Modified to strictly allow boolean for saveToHousehold logic or record for general usage
  additionalInformation: z.record(z.string(), z.unknown()).nullable().optional(),
  filterGroup: FilterGroupSchema.nullable().optional(),
  sync: ContactSyncSchema.nullable().optional(),
  relatedObjects: z.record(z.string(), RelatedObjectSchema).nullable().optional(),
  operationType: OperationType.nullable().optional(),
  contactLinks: z.array(ContactLinkSchema).nullable().optional(),
  mappingType: SessionMappingType.nullable().optional(),
  constraints: z.record(z.string(), FieldConstraintSchema).nullable().optional(),
  limit: z.number().nullable().optional(),
  isHousehold: z.boolean().nullable().optional(),
  orderBy: OrderByListSchema.nullable().optional(),
  managedBy: z.string().nullable().optional(),
})

export const PlainCustomObjectFieldMappingSchema = z.object({
  type: z.literal('field'),
  objectName: z.string(),
  description: z.string().nullable().optional(),
})

export const ReferenceCustomObjectFieldMappingSchema = z.object({
  type: z.literal('reference'),
  objectName: z.string(),
  description: z.string().nullable().optional(),
  referenceObjectName: z.string(),
  referenceFields: z.array(z.string()).nullable().optional(),
})

export const CustomObjectFieldMappingSchema = z.discriminatedUnion('type', [PlainCustomObjectFieldMappingSchema, ReferenceCustomObjectFieldMappingSchema])

export const CustomObjectMappingSchema = z.object({
  objectName: z.string(),
  displayName: z.string().nullable().optional(),
  fields: z.array(CustomObjectFieldMappingSchema).nullable().optional(),
  contactLinks: z.array(ContactLinkSchema).nullable().optional(),
  contactLinkField: z.string().nullable().optional(),
  referenceFields: z.array(z.string()).nullable().optional(),
  filterGroup: FilterGroupSchema.nullable().optional(),
  isHousehold: z.boolean().nullable().optional(),
  managedBy: z.string().nullable().optional(),
})

const MappingsSchema = z.object({
  session: DetailedCRMAccountIntegrationMappingSchema.nullable().optional(),
  note: DetailedCRMAccountIntegrationMappingSchema.nullable().optional(),
  result: DetailedCRMAccountIntegrationMappingSchema.nullable().optional(),
  contact: DetailedCRMAccountIntegrationMappingSchema.nullable().optional(),
  contactFields: DetailedCRMAccountIntegrationMappingSchema.nullable().optional(),
  calendar: DetailedCRMAccountIntegrationMappingSchema.nullable().optional(),
  documentSummary: DetailedCRMAccountIntegrationMappingSchema.nullable().optional(),
  task: DetailedCRMAccountIntegrationMappingSchema.nullable().optional(),
  additionalObjects: z.array(DetailedCRMAccountIntegrationMappingSchema.nullable().optional()).nullable().optional(),
  businessEvents: z.partialRecord(z.nativeEnum(BusinessEventType), DetailedCRMAccountIntegrationMappingSchema.nullable().optional()).nullable().optional(),
  customObjects: z.array(CustomObjectMappingSchema).nullable().optional(),
})

const BaseIntegrationSettingsSchema = z.object({
  type: z.string().optional(),
  serviceId: z.string(),
  loginUrl: z
    .string()
    .nullable()
    .optional()
    .refine(
      (val) => {
        if (!val) return true
        try {
          const url = new URL(val)
          return url.protocol === 'https:' && url.pathname === '/' && !url.search && !url.hash
        } catch {
          return false
        }
      },
      { message: 'Use only the base web address, with nothing after the domain.' },
    ),
  mappings: MappingsSchema, // Nested mappings
  enableNoteExport: z.boolean().nullable().optional(),
  enableFullMeetingExport: z.boolean().nullable().optional(),
  enableContactCreate: z.boolean().nullable().optional(),
  enableContactUpdate: z.boolean().nullable().optional(),
  isCustom: z.boolean().nullable().optional(),
  isSyncDisabled: z.boolean().nullable().optional(),
  enabledBusinessEventTypes: z.array(z.nativeEnum(BusinessEventType)).nullable().optional(),
})

export const getIntegrationSettingsSchema = (additionalObjectsLimit: number, requireMappings = true) => {
  if (!requireMappings) {
    return BaseIntegrationSettingsSchema
  }
  return BaseIntegrationSettingsSchema.superRefine((data, ctx) => {
    const { mappings } = data
    const integrationConfig = data as unknown as CRMAccountIntegrationConfig
    const isEditable = (mappingType: AccountIntegrationConfigMappingType) => !isMappingTypeLocked(integrationConfig, mappingType)

    if (!mappings.contact) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Contact mapping is required',
        path: ['mappings', 'contact'],
      })
    }

    if (!mappings.session) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Meeting Summary mapping is required',
        path: ['mappings', 'session'],
      })
    }

    if (mappings.contact && isEditable(AccountIntegrationConfigMappingType.CONTACT)) {
      if (!mappings.contact.objectName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Contact object name is required',
          // Path updated to include 'mappings'
          path: ['mappings', 'contact', 'objectName'],
        })
      }

      const contactFields = mappings.contact.fields

      // 2. Look for the email mapping
      const emailDescriptors = contactFields?.['email']
      const emailValue = emailDescriptors?.[0]?.value

      // 3. Validation Logic
      if (mappings.contact.mappingType && (!emailValue || emailValue.trim() === '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'E-mail mapping is required',
          // Path updated to include 'mappings'
          path: ['mappings', 'contact', 'fields', 'email'],
        })
      }
    }

    if (mappings.session && isEditable(AccountIntegrationConfigMappingType.SESSION)) {
      if (!mappings.session.objectName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Meeting Summary object name is required',
          // Path updated to include 'mappings'
          path: ['mappings', 'session', 'objectName'],
        })
      }

      const contactFields = mappings.contact?.fields

      const additionalInfo = mappings.session.additionalInformation as Record<string, unknown> | undefined
      const saveToHousehold = additionalInfo?.saveToHousehold === true

      const isPredefinedOrEmpty = mappings.session.mappingType === 'PREDEFINED' || !mappings.session.mappingType

      if (isPredefinedOrEmpty && saveToHousehold) {
        const householdDescriptors = contactFields?.['household']
        const householdValue = householdDescriptors?.[0]?.value

        if (!householdValue || householdValue.trim() === '') {
          ctx.addIssue(
            isEditable(AccountIntegrationConfigMappingType.CONTACT)
              ? {
                  code: z.ZodIssueCode.custom,
                  message: 'Household field must be mapped when "Save to Household" is enabled in Session settings.',
                  path: ['mappings', 'contact', 'fields', 'household'],
                }
              : {
                  code: z.ZodIssueCode.custom,
                  message: `"Save to Household" needs a household field in the custom-built ${getMappingTypeLabel(AccountIntegrationConfigMappingType.CONTACT)} mapping.`,
                  path: ['mappings', 'session', 'additionalInformation', 'saveToHousehold'],
                },
          )
        }
      }

      if (isPredefinedOrEmpty && mappings.contact?.mappingType === 'CUSTOM') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Session mapping must be set to Custom when Contact mapping is Custom.',
          path: ['mappings', 'session'],
        })
      }

      if (mappings.session.operationType === AccountOperationType.UPDATE && !isEditable(AccountIntegrationConfigMappingType.ADDITIONAL_OBJECTS)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Updating existing records doesn't work while the ${getMappingTypeLabel(AccountIntegrationConfigMappingType.ADDITIONAL_OBJECTS)} mapping is custom-built. Switch this option off.`,
          path: ['mappings', 'session', 'operationType'],
        })
      }

      if (mappings.session.mappingType === MappingType.CUSTOM) {
        if (Object.keys(mappings.session.contactLinks || {}).length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one Contact Linking must be defined for Session when using Custom mapping type.',
            path: ['mappings', 'session', 'contactLinks'],
          })
        }

        if (mappings.session.operationType === 'update') {
          const updateTitle = (mappings.session.additionalInformation?.update as UpdateAdditionalInformation)?.title
          if (updateTitle === undefined || updateTitle === null) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Title field must be defined if type is set to UPDATE.',
              path: ['mappings', 'session', 'additionalInformation', 'update', 'title'],
            })
          }
          const updateIdKey = (mappings.session.additionalInformation?.update as UpdateAdditionalInformation)?.idKey
          if (
            (data.serviceId.toLowerCase() === 'dynamics' || data.serviceId.toLowerCase() === 'engage') &&
            (updateIdKey === undefined || updateIdKey === null)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Id field must be defined if type is set to UPDATE.',
              path: ['mappings', 'session', 'additionalInformation', 'update', 'idKey'],
            })
          }
        }

        if (mappings.session.contactLinks && mappings.session.contactLinks.length > 0) {
          const notFullyDefinedLinks = mappings.session.contactLinks.filter((link) => {
            const sourceObj = link.source?.object
            const sourceField = link.source?.field
            const targetObj = link.target?.object
            const targetField = link.target?.field
            return !sourceObj || !sourceField || !targetObj || !targetField
          })
          if (notFullyDefinedLinks.length > 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'All Contact Linking source and target objects and fields must be fully defined.',
              path: ['mappings', 'session', 'contactLinks'],
            })
          }
        }

        if (Object.keys(mappings.session.fields || {}).length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one field must be mapped for Session when using Custom mapping type.',
            path: ['mappings', 'session', 'fields'],
          })
        }

        if (mappings.session.operationType === AccountOperationType.UPDATE) {
          const sessionFieldsWithoutUpdateType = Object.values(mappings.session.fields || {})
            .flat()
            .filter((descriptor) => !descriptor.updateMode)

          if (sessionFieldsWithoutUpdateType.length > 0) {
            sessionFieldsWithoutUpdateType.forEach((descriptor) => {
              const fieldName = Object.entries(mappings.session?.fields || {}).find(([, descriptors]) => descriptors.includes(descriptor))?.[0]
              if (fieldName) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'All Session fields must have an update mode defined when operation type is set to UPDATE.',
                  path: ['mappings', 'session', 'fields', fieldName],
                })
              }
            })
          }
        }

        const sessionFieldsNothingMapped = Object.entries(mappings.session.fields || {}).reduce<string[]>((acc, [fieldName, descriptors]) => {
          if (descriptors.length === 0) {
            return [...acc, fieldName]
          }
          return acc
        }, [])
        if (sessionFieldsNothingMapped.length > 0) {
          sessionFieldsNothingMapped.forEach((fieldName) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'All Session fields must have at least one mapped value when using Custom mapping type.',
              path: ['mappings', 'session', 'fields', fieldName],
            })
          })
        }
      }
    }

    if (mappings.calendar && isEditable(AccountIntegrationConfigMappingType.CALENDAR) && mappings.calendar.mappingType === MappingType.CUSTOM) {
      const calendar = mappings.calendar

      if (!calendar.objectName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Calendar object name is required',
          path: ['mappings', 'calendar', 'objectName'],
        })
      }

      ZOCKS_EVENT_FIELDS.filter((field) => field.required).forEach((field) => {
        const descriptors = (calendar.fields?.[field.zocksKey] ?? []).filter((descriptor) => (descriptor.value ?? '').trim() !== '')
        const isMapped = descriptors.some(
          (descriptor) => descriptor.type === DescriptorType.enum.field || (descriptor.type === DescriptorType.enum.static && field.allowStaticValue),
        )
        if (!isMapped) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: descriptors.length > 0 ? `${field.label} must be mapped to a field` : `${field.label} is required`,
            path: ['mappings', 'calendar', 'fields', field.zocksKey],
          })
        }
      })

      const hasCompleteContactLink = (calendar.contactLinks ?? []).some((link) => isCompleteLinkFor(link, calendar.objectName ?? ''))
      if (!hasCompleteContactLink) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one Contact Linking must be defined for the calendar object.',
          path: ['mappings', 'calendar', 'contactLinks'],
        })
      }
    }

    if (mappings.task && isEditable(AccountIntegrationConfigMappingType.TASK)) {
      if (!mappings.task.objectName && mappings.task.mappingType === MappingType.CUSTOM) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Task object name is required',
          path: ['mappings', 'task', 'objectName'],
        })
      }

      if (mappings.task.mappingType === MappingType.CUSTOM) {
        if (Object.keys(mappings.task.contactLinks || {}).length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one Contact Linking must be defined for Task when using Custom mapping type.',
            path: ['mappings', 'task', 'contactLinks'],
          })
        }

        if (mappings.task.contactLinks && mappings.task.contactLinks.length > 0) {
          const notFullyDefinedLinks = mappings.task.contactLinks.filter((link) => {
            const sourceObj = link.source?.object
            const sourceField = link.source?.field
            const targetObj = link.target?.object
            const targetField = link.target?.field
            return !sourceObj || !sourceField || !targetObj || !targetField
          })
          if (notFullyDefinedLinks.length > 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'All Contact Linking source and target objects and fields must be fully defined.',
              path: ['mappings', 'task', 'contactLinks'],
            })
          }
        }

        if (Object.keys(mappings.task.fields || {}).length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one field must be mapped for Task when using Custom mapping type.',
            path: ['mappings', 'task', 'fields'],
          })
        }

        const taskFieldsNothingMapped = Object.entries(mappings.task.fields || {}).reduce<string[]>((acc, [fieldName, descriptors]) => {
          if (descriptors.length === 0) {
            return [...acc, fieldName]
          }
          return acc
        }, [])
        if (taskFieldsNothingMapped.length > 0) {
          taskFieldsNothingMapped.forEach((fieldName) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'All Task fields must have at least one mapped value when using Custom mapping type.',
              path: ['mappings', 'task', 'fields', fieldName],
            })
          })
        }
      }
    }

    if (mappings.additionalObjects && isEditable(AccountIntegrationConfigMappingType.ADDITIONAL_OBJECTS)) {
      const additionalObjectMappingsWithoutObjectName = mappings.additionalObjects.filter((obj) => !obj?.objectName && obj?.mappingType === MappingType.CUSTOM)
      if (additionalObjectMappingsWithoutObjectName.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'All Additional Objects must have an object defined.',

          path: ['mappings', 'additionalObjects'],
        })
      }

      const additionalObjectMappingWithoutFields = mappings.additionalObjects.filter((obj) => {
        return obj?.mappingType === MappingType.CUSTOM && (!obj.fields || Object.keys(obj.fields).length === 0)
      })
      if (additionalObjectMappingWithoutFields.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'All Additional Objects must have at least one field mapped.',
          path: ['mappings', 'additionalObjects'],
        })
      }

      const sumAdditionalObjectsCount = mappings.additionalObjects ? mappings.additionalObjects.map((obj) => obj?.limit || 0).reduce((a, b) => a + b, 0) : 0

      if (sumAdditionalObjectsCount > additionalObjectsLimit) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `The total limit for Additional Objects exceeds the allowed maximum of ${additionalObjectsLimit}.`,
          path: ['mappings', 'additionalObjects'],
        })
      }

      const objectWithZeroLimit = mappings.additionalObjects.find((obj) => (obj?.limit || 0) < 1)
      if (objectWithZeroLimit) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Each Additional Object must have a limit of at least 1.',
          path: ['mappings', 'additionalObjects'],
        })
      }

      if (mappings.additionalObjects.some((obj) => !obj?.contactLinks || obj.contactLinks.length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one Contact Linking must be defined for the object.',
          path: ['mappings', 'additionalObjects'],
        })
      }

      const additionalObjectWithoutProperContactLinking = mappings.additionalObjects.some((obj) => {
        return obj?.contactLinks?.some((link) => {
          return link.source?.object === null || link.source?.field === null || link.target?.object === null || link.target?.field === null
        })
      })
      if (additionalObjectWithoutProperContactLinking) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'All Contact Linking source and target objects and fields must be fully defined.',
          path: ['mappings', 'additionalObjects'],
        })
      }
    }

    if (mappings.customObjects && isEditable(AccountIntegrationConfigMappingType.CUSTOM_OBJECTS)) {
      const customObjectsWithoutObjectName = mappings.customObjects.filter((obj) => !obj?.objectName)
      if (customObjectsWithoutObjectName.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'All Objects must have an object type selected.',
          path: ['mappings', 'customObjects'],
        })
      }

      const customObjectsWithoutFields = mappings.customObjects.filter((obj) => !!obj?.objectName && !obj.fields?.length)
      if (customObjectsWithoutFields.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'All Objects must have at least one field added.',
          path: ['mappings', 'customObjects'],
        })
      }

      const customObjectsWithIncompleteContactLink = mappings.customObjects.filter((obj) =>
        (obj?.contactLinks ?? []).some((link) => Boolean(link?.source?.field) !== Boolean(link?.target?.field)),
      )
      if (customObjectsWithIncompleteContactLink.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Contact Linking must have a field selected on both sides.',
          path: ['mappings', 'customObjects'],
        })
      }
    }

    if (mappings.businessEvents && isEditable(AccountIntegrationConfigMappingType.BUSINESS_EVENTS)) {
      Object.entries(mappings.businessEvents).forEach(([eventType, eventMapping]) => {
        if (!eventMapping || eventMapping.mappingType !== MappingType.CUSTOM || !eventMapping.objectName) {
          return
        }

        if (!eventMapping.contactLinks || eventMapping.contactLinks.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one Contact Linking must be defined.',
            path: ['mappings', 'businessEvents', eventType, 'contactLinks'],
          })
        }

        if (eventMapping.contactLinks && eventMapping.contactLinks.length > 0) {
          const notFullyDefinedLinks = eventMapping.contactLinks.filter((link) => {
            return !link.source?.object || !link.source?.field || !link.target?.object || !link.target?.field
          })
          if (notFullyDefinedLinks.length > 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'All Contact Linking source and target objects and fields must be fully defined.',
              path: ['mappings', 'businessEvents', eventType, 'contactLinks'],
            })
          }
        }

        if (!eventMapping.fields || Object.keys(eventMapping.fields).length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one field must be mapped.',
            path: ['mappings', 'businessEvents', eventType, 'fields'],
          })
        }

        const fieldsWithNoDescriptors = Object.entries(eventMapping.fields || {})
          .filter(([, descriptors]) => !descriptors || descriptors.length === 0)
          .map(([fieldName]) => fieldName)
        fieldsWithNoDescriptors.forEach((fieldName) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'All fields must have at least one mapped value.',
            path: ['mappings', 'businessEvents', eventType, 'fields', fieldName],
          })
        })
      })
    }
  })
}
export type IntegrationSettings = z.infer<typeof BaseIntegrationSettingsSchema>
